import React, { useState, useEffect, useRef } from 'react';
import init, { decrypt_ciphertext_with_randomness, decode_transfer_proof } from '../pkg/confidential_transfer_proof_generation.js';
import { getChainDisplayNameForRequest } from '../config/chains';
import { obtainUnlockedIbeKeyForIdentity } from '../services/obtainIbeKey';
import { decryptTransactionAmount, getEncryptedRandomnessForRole } from '../utils/transactionDecryption';
import { formatAddress } from '../utils/address';
import { toUserFriendlyError } from '../utils/friendlyError';
import KvDecryptionModal from './KvDecryptionModal';
import Modal from './Modal';
import './DecryptionModal.css';
import './Modal.css';

const DecryptionModal = ({ transaction, userAddress, auditorData, onClose, onDecryptionComplete }) => {
  const [step, setStep] = useState('request'); // 'request', 'polling', 'decrypting', 'complete'
  const [error, setError] = useState(null);
  const [decryptedAmount, setDecryptedAmount] = useState(null);
  const [wasmModule, setWasmModule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pollingStatus, setPollingStatus] = useState(null); // { elapsed: number }
  const [showKvDecryption, setShowKvDecryption] = useState(false);
  const [decryptPhase, setDecryptPhase] = useState(null); // ibe_randomness | rpc_fetch | decode_proof | elgamal
  const pollAbortRef = useRef(null);
  const pollStartedAtRef = useRef(null);
  const ibeKeyCacheRef = useRef(new Map());

  const decryptPhaseLabel = (phase) => {
    switch (phase) {
      case 'ibe_randomness':
        return 'Decrypting IBE randomness…';
      case 'rpc_fetch':
        return 'Fetching transaction from chain RPC…';
      case 'decode_proof':
        return 'Decoding transfer proof…';
      case 'elgamal':
        return 'Decrypting ElGamal ciphertext…';
      default:
        return 'Decrypting IBE randomness…';
    }
  };

  useEffect(() => {
    const initWasm = async () => {
      try {
        await init();
        setWasmModule({ decrypt_ciphertext_with_randomness, decode_transfer_proof });
      } catch (err) {
        console.error('Failed to initialize WASM:', err);
        setError('Failed to initialize decryption module');
      }
    };
    initWasm();
  }, []);

  // Tick elapsed time once per second while waiting — do not depend on poll
  // attempt callbacks (Firestore scans can block those for many seconds).
  useEffect(() => {
    if (step !== 'polling') {
      pollStartedAtRef.current = null;
      return undefined;
    }
    if (pollStartedAtRef.current == null) {
      pollStartedAtRef.current = Date.now();
      setPollingStatus({ elapsed: 0 });
    }
    const id = setInterval(() => {
      const started = pollStartedAtRef.current || Date.now();
      setPollingStatus({ elapsed: Date.now() - started });
    }, 1000);
    return () => clearInterval(id);
  }, [step]);

  const ensureIbeKey = async (pepIdentity, { which } = {}) => {
    const cacheKey = String(pepIdentity).toLowerCase();
    if (ibeKeyCacheRef.current.has(cacheKey)) {
      return ibeKeyCacheRef.current.get(cacheKey);
    }

    setStep('polling');
    console.log('[DecryptionModal] Requesting decryption key for identity:', {
      which,
      pepIdentityPreview: `${String(pepIdentity).substring(0, 24)}…`,
      transactionId: transaction?.id
    });

    const pollAbort = new AbortController();
    pollAbortRef.current = pollAbort;

    const unlocked = await obtainUnlockedIbeKeyForIdentity({
      pepIdentity,
      auditorIdentity: auditorData.uid || auditorData.id,
      secpPublicKey: auditorData.publicKey,
      lookupHints: {
        chainTxHash: transaction.transactionHash || transaction.txHash || null,
        extraAuditorIds: [auditorData.grantId, auditorData.id].filter(
          (x) => typeof x === 'string' && x.length > 0
        ),
        requestId: transaction.id || null
      },
      abortSignal: pollAbort.signal,
      onPhase: (phase) => {
        if (phase === 'polling') setStep('polling');
      }
    });

    ibeKeyCacheRef.current.set(cacheKey, unlocked);
    setStep('decrypting');
    return unlocked;
  };

  const handleRequestKey = async () => {
    setError(null);
    setLoading(true);

    try {
      if (!auditorData || !auditorData.publicKey) {
        throw new Error('Auditor public key not found. Please contact administrator.');
      }
      const auditorIdentity = auditorData.uid || auditorData.id;
      if (!auditorIdentity) {
        throw new Error('Auditor UID not found. Please contact administrator.');
      }

      const addressForDecrypt = transaction.userAddress || userAddress;
      if (!addressForDecrypt) {
        throw new Error('Transaction user address not found');
      }

      const { active, queued } = getEncryptedRandomnessForRole(transaction, addressForDecrypt);
      if (!active.ciphertext && !queued.ciphertext) {
        throw new Error('No encrypted randomness found for this transaction');
      }

      // Prefer the active identity; decrypt path will request the queued identity
      // if active ciphertext fails (e.g. chain pk rotation).
      const initialPepIdentity = active.pepIdentity || queued.pepIdentity;
      if (!initialPepIdentity) {
        throw new Error('No IBE identity found for this transaction');
      }

      await ensureIbeKey(initialPepIdentity, {
        which: active.ciphertext ? 'active' : 'queued'
      });
      await handleDecrypt(addressForDecrypt);
    } catch (err) {
      console.error('Key request error:', err);
      setError(toUserFriendlyError(err, 'Could not request a decryption key. Please try again.'));
      setStep('request');
      setLoading(false);
      pollAbortRef.current = null;
    }
  };

  const handleCancelPolling = () => {
    pollAbortRef.current?.abort();
    pollAbortRef.current = null;
    setStep('request');
    setLoading(false);
    setPollingStatus(null);
    setError(null);
  };

  const handleDecrypt = async (addressForDecrypt) => {
    if (!wasmModule) {
      setError('The decryption module is still loading. Please wait a moment and try again.');
      setLoading(false);
      return;
    }

    setStep('decrypting');
    setDecryptPhase('ibe_randomness');
    setError(null);

    try {
      const viewingAddress = addressForDecrypt || transaction.userAddress || userAddress;
      if (!viewingAddress) {
        throw new Error('User address is required for decryption. Transaction has no user address and no user is selected.');
      }

      const getIbeKey = async (_identity, meta = {}) => {
        const pepIdentity = meta.pepIdentity || _identity;
        return ensureIbeKey(pepIdentity, { which: meta.which });
      };

      const { displayAmount } = await decryptTransactionAmount(
        transaction,
        viewingAddress,
        getIbeKey,
        wasmModule,
        { onProgress: setDecryptPhase }
      );

      setDecryptedAmount(displayAmount);
      setStep('complete');
      onDecryptionComplete(transaction.id, displayAmount);
    } catch (err) {
      console.error('Decryption error:', err);
      setError('decryption error');
      setStep('request');
    } finally {
      setDecryptPhase(null);
      setLoading(false);
    }
  };

  const busy = loading || step === 'polling' || step === 'decrypting';
  const kvBrowseIdentity =
    getEncryptedRandomnessForRole(transaction, transaction.userAddress || userAddress).active
      .pepIdentity ||
    transaction.userAddress ||
    userAddress;

  return (
    <>
      <Modal
        title="Decrypt Transfer Amount"
        onClose={onClose}
        dismissible={!busy}
      >
          <div className="transaction-info">
            <div className="info-row">
              <span className="info-label">User:</span>
              <span className="info-value">{formatAddress(transaction.userAddress || userAddress)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Transaction:</span>
              <span className="info-value">{formatAddress(transaction.transactionHash)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Role:</span>
              <span className="info-value">{transaction.role === 'sender' ? 'Sender' : 'Recipient'}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Chain:</span>
              <span className="info-value">{getChainDisplayNameForRequest(transaction)}</span>
            </div>
          </div>

          {step === 'request' && (
            <div className="decryption-step">
              <h3>Request Decryption Key</h3>
              <p className="step-description">
                Request the decryption key for this transaction. The system will check for an existing key,
                or request a new one from the compliance server and wait for it to be ready.
              </p>

              {error && <div className="error-message" role="alert">{error}</div>}

              <div className="modal-actions">
                <button type="button" onClick={onClose} className="button-secondary">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleRequestKey}
                  className="button-primary"
                  disabled={loading || !wasmModule}
                >
                  {!wasmModule ? 'Loading…' : loading ? 'Requesting Key…' : 'Request Key & Decrypt'}
                </button>
              </div>
            </div>
          )}

          {step === 'polling' && (
            <div className="decryption-step">
              <div className="polling-state busy-state" role="status" aria-live="polite">
                <div className="loading-spinner" aria-hidden="true"></div>
                <h3>Waiting for Decryption Key</h3>
                <p className="step-description">
                  The decryption key request has been submitted. Waiting for it to be processed…
                </p>
                {pollingStatus && (
                  <div className="polling-status">
                    <p>Elapsed time: {(pollingStatus.elapsed / 1000).toFixed(0)}s</p>
                  </div>
                )}
                {error && <div className="error-message" role="alert">{error}</div>}
                <div className="modal-actions">
                  <button type="button" className="button-secondary" onClick={handleCancelPolling}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 'decrypting' && (
            <div className="decryption-step">
              <div className="decrypting-state busy-state" role="status" aria-live="polite">
                <div className="loading-spinner" aria-hidden="true"></div>
                <p>Decrypting transfer amount…</p>
                <p className="decrypting-steps">
                  <span className="step-done">Key unlocked</span><br />
                  {decryptPhase && decryptPhase !== 'ibe_randomness' && (
                    <>
                      <span className="step-done">IBE randomness decrypted</span><br />
                    </>
                  )}
                  {decryptPhase &&
                    decryptPhase !== 'ibe_randomness' &&
                    decryptPhase !== 'rpc_fetch' && (
                      <>
                        <span className="step-done">Transaction fetched</span><br />
                      </>
                    )}
                  <span className="step-active">{decryptPhaseLabel(decryptPhase)}</span>
                </p>
              </div>
            </div>
          )}

          {step === 'complete' && (
            <div className="decryption-step">
              <div className="success-state" role="status" aria-live="polite">
                <div className="success-icon" aria-hidden="true" />
                <h3>Decryption Complete</h3>
                <div className="decrypted-amount-display">
                  <span className="amount-label">Transfer Amount:</span>
                  <span className="amount-value">{decryptedAmount}</span>
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    onClick={() => setShowKvDecryption(true)}
                    className="button-secondary"
                  >
                    Decrypt KV Entries
                  </button>
                  <button type="button" onClick={onClose} className="button-primary">
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
      </Modal>

      {showKvDecryption && (
        <KvDecryptionModal
          auditorIdentity={auditorData?.uid || auditorData?.id}
          userAddress={kvBrowseIdentity}
          onClose={() => setShowKvDecryption(false)}
        />
      )}
    </>
  );
};

export default DecryptionModal;
